import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import api from '../../../api.js';

function WorkingSiteFormModal({ isOpen, onClose, onSuccess, onMessage, editingSite, organizations }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    org_id: '',
    site_code: '',
    site_name: '',
    site_type: 'depot',
    address: '',
    postal_code: '',
    city: '',
    country: 'FR',
    gps_latitude: '',
    gps_longitude: '',
    is_primary: false,
    parking_capacity: '',
    has_maintenance_facility: false,
    has_fuel_station: false,
    contact_name: '',
    contact_phone: '',
    operating_hours: '',
    status: 'active',
  });

  useEffect(() => {
    if (editingSite) {
      setFormData({
        org_id: editingSite.org_id || '',
        site_code: editingSite.site_code || '',
        site_name: editingSite.site_name || '',
        site_type: editingSite.site_type || 'depot',
        address: editingSite.address || '',
        postal_code: editingSite.postal_code || '',
        city: editingSite.city || '',
        country: editingSite.country || 'FR',
        gps_latitude: editingSite.gps_latitude || '',
        gps_longitude: editingSite.gps_longitude || '',
        is_primary: editingSite.is_primary || false,
        parking_capacity: editingSite.parking_capacity || '',
        has_maintenance_facility: editingSite.has_maintenance_facility || false,
        has_fuel_station: editingSite.has_fuel_station || false,
        contact_name: editingSite.contact_name || '',
        contact_phone: editingSite.contact_phone || '',
        operating_hours: editingSite.operating_hours || '',
        status: editingSite.status || 'active',
      });
    }
  }, [editingSite]);

  // Normalize GPS coordinate - handle comma as decimal separator
  const normalizeCoord = (value) => {
    if (!value && value !== 0) return null;
    // Replace comma with period (French locale)
    const normalized = String(value).replace(',', '.');
    const num = parseFloat(normalized);
    return isNaN(num) ? null : num;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (submitData.parking_capacity === '') delete submitData.parking_capacity;

      // Properly parse GPS coordinates
      const lat = normalizeCoord(submitData.gps_latitude);
      const lng = normalizeCoord(submitData.gps_longitude);

      if (lat !== null && (lat < -90 || lat > 90)) {
        onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.siteForm.latInvalid'), type: 'error' });
        return;
      }
      if (lng !== null && (lng < -180 || lng > 180)) {
        onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.siteForm.lngInvalid'), type: 'error' });
        return;
      }

      submitData.gps_latitude = lat;
      submitData.gps_longitude = lng;
      if (lat === null) delete submitData.gps_latitude;
      if (lng === null) delete submitData.gps_longitude;

      if (editingSite) {
        await api.workingSite.update(editingSite.id, submitData);
        onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.siteForm.successUpdate'), type: 'success' });
      } else {
        await api.workingSite.create(submitData);
        onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.siteForm.successCreate'), type: 'success' });
      }
      onSuccess();
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.siteForm.errorPrefix', { message: error.message }), type: 'error' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {editingSite ? t('settings.siteForm.editTitle') : t('settings.siteForm.createTitle')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.orgLabel')}</label>
              <select
                required
                value={formData.org_id}
                onChange={(e) => setFormData({ ...formData, org_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="">{t('common.actions.select')}</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.code}>{org.name} ({org.code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.siteCodeLabel')}</label>
              <input
                type="text"
                required
                value={formData.site_code}
                onChange={(e) => setFormData({ ...formData, site_code: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder={t('settings.siteForm.siteCodePlaceholder')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.siteNameLabel')}</label>
              <input
                type="text"
                required
                value={formData.site_name}
                onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.typeLabel')}</label>
              <select
                value={formData.site_type}
                onChange={(e) => setFormData({ ...formData, site_type: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                {/* Types de site : clés partagées `sites.types.*`, ne pas recréer de libellés locaux (cf. incohérence "Siège"/"Head office") */}
                <option value="siege">{t('sites.types.siege')}</option>
                <option value="agence">{t('sites.types.agence')}</option>
                <option value="depot">{t('sites.types.depot')}</option>
                <option value="parking">{t('sites.types.parking')}</option>
                <option value="atelier">{t('sites.types.atelier')}</option>
                <option value="station">{t('sites.types.station')}</option>
                <option value="client">{t('sites.types.client')}</option>
                <option value="chantier">{t('sites.types.chantier')}</option>
                <option value="autre">{t('sites.types.autre')}</option>
              </select>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.addressLabel')}</label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.postalCodeLabel')}</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.cityLabel')}</label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.countryLabel')}</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          {/* GPS Coordinates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.latLabel')}</label>
              <input
                type="number"
                step="0.000001"
                value={formData.gps_latitude}
                onChange={(e) => setFormData({ ...formData, gps_latitude: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="48.8566"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.lngLabel')}</label>
              <input
                type="number"
                step="0.000001"
                value={formData.gps_longitude}
                onChange={(e) => setFormData({ ...formData, gps_longitude: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="2.3522"
              />
            </div>
          </div>

          {/* Facilities */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.capacityLabel')}</label>
              <input
                type="number"
                value={formData.parking_capacity}
                onChange={(e) => setFormData({ ...formData, parking_capacity: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder={t('settings.siteForm.capacityPlaceholder')}
              />
            </div>
            <div className="flex items-center space-x-6 pt-8">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_primary}
                  onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                  className="w-4 h-4 text-amber-500 rounded focus:ring-2 focus:ring-amber-500"
                />
                <span className="ml-2 text-sm font-semibold text-gray-700">{t('settings.siteForm.primaryLabel')}</span>
              </label>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.has_maintenance_facility}
                onChange={(e) => setFormData({ ...formData, has_maintenance_facility: e.target.checked })}
                className="w-4 h-4 text-amber-500 rounded focus:ring-2 focus:ring-amber-500"
              />
              <span className="ml-2 text-sm font-semibold text-gray-700">{t('settings.siteForm.maintenanceLabel')}</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.has_fuel_station}
                onChange={(e) => setFormData({ ...formData, has_fuel_station: e.target.checked })}
                className="w-4 h-4 text-amber-500 rounded focus:ring-2 focus:ring-amber-500"
              />
              <span className="ml-2 text-sm font-semibold text-gray-700">{t('settings.siteForm.fuelStationLabel')}</span>
            </label>
          </div>

          {/* Contact & Hours */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.contactLabel')}</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.contactPhoneLabel')}</label>
              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.hoursLabel')}</label>
              <input
                type="text"
                value={formData.operating_hours}
                onChange={(e) => setFormData({ ...formData, operating_hours: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder={t('settings.siteForm.hoursPlaceholder')}
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.siteForm.statusLabel')}</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="active">{t('settings.siteForm.siteStatus.active')}</option>
              <option value="inactive">{t('settings.siteForm.siteStatus.inactive')}</option>
              <option value="closed">{t('settings.siteForm.siteStatus.closed')}</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition"
            >
              {editingSite ? t('common.actions.update') : t('common.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default WorkingSiteFormModal;
